import React, { useState, useEffect } from 'react';
import { motion, } from 'framer-motion';
import LoanRequestDialog from '../../components/Dialog/LoanRequestDialog';
import * as Slider from '@radix-ui/react-slider';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function HomeLoan() {
  const [loanAmount, setLoanAmount] = useState(2500000); 
  const [interestRate, setInterestRate] = useState(7.9); 
  const [loanTerm, setLoanTerm] = useState(30); 

  const [monthlyEMI, setMonthlyEMI] = useState(18170);
  const [totalInterest, setTotalInterest] = useState(4041249);
  const [totalPayment, setTotalPayment] = useState(6541249);

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, loanTerm]);

  const calculateEMI = () => {
    if (loanAmount > 0 && interestRate >= 0 && loanTerm > 0) {
      const monthlyInterestRate = interestRate / 100 / 12;
      const numberOfMonths = loanTerm * 12;

      let emi;
      if (monthlyInterestRate > 0) {
        const numerator = loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfMonths);
        const denominator = Math.pow(1 + monthlyInterestRate, numberOfMonths) - 1;
        emi = numerator / denominator;
      } else {
        emi = loanAmount / numberOfMonths;
      }

      const totalAmount = emi * numberOfMonths;
      const totalInterestPayable = totalAmount - loanAmount;

      setMonthlyEMI(emi);
      setTotalPayment(totalAmount);
      setTotalInterest(totalInterestPayable);
    }
  };

  const resultsVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, delay: 0.2, ease: "easeOut" } },
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        {/* Left Section: Inputs & Assistance */}
        <div className="space-y-6">
          {/* Loan Amount */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Loan Amount</label>
            <div className="flex items-center">
              <Slider.Root 
                className="relative flex items-center select-none touch-none w-full h-5 mt-2"
                defaultValue={[loanAmount]}
                max={10000000}
                min={100000}
                step={10000}
                onValueChange={(value) => setLoanAmount(value[0])}
              >
                <Slider.Track className="bg-red-500/40 relative flex-grow rounded-full h-1.5">
                  <Slider.Range className="absolute bg-red-500 rounded-full h-full" />
                </Slider.Track>
                <Slider.Thumb className="block w-4 h-4 bg-accent border-2 border-white rounded-full shadow-lg hover:bg-primary-700 focus:outline-none " />
              </Slider.Root>
              <span className="ml-4 w-32 text-right font-medium text-gray-800">
                ₹ {loanAmount.toLocaleString('en-IN')}
              </span>
            </div>
          </div>

          {/* Tenure */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Tenure (Years)</label>
            <div className="flex items-center">
              <Slider.Root 
                className="relative flex items-center select-none touch-none w-full h-5 mt-2"
                defaultValue={[loanTerm]}
                max={30}
                min={1}
                step={1}
                onValueChange={(value) => setLoanTerm(value[0])}
              >
                <Slider.Track className="bg-red-500/40 relative flex-grow rounded-full h-1.5">
                  <Slider.Range className="absolute bg-red-500 rounded-full h-full" />
                </Slider.Track>
                <Slider.Thumb className="block w-4 h-4 bg-accent border-2 border-white rounded-full shadow-lg hover:bg-primary-700 focus:outline-none " />
              </Slider.Root>
              <span className="ml-4 w-12 text-right font-medium text-gray-800">
                {loanTerm} Yrs
              </span>
            </div>
          </div>

          {/* Interest Rate */}
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Interest Rate (% P.A.)</label>
            <div className="flex items-center">
              <Slider.Root 
                className="relative flex items-center select-none touch-none w-full h-5 mt-2"
                defaultValue={[interestRate]}
                max={15}
                min={0.5}
                step={0.1}
                onValueChange={(value) => setInterestRate(value[0])}
              >
                <Slider.Track className="bg-red-500/40 relative flex-grow rounded-full h-1.5">
                  <Slider.Range className="absolute bg-red-500 rounded-full h-full" />
                </Slider.Track>
                <Slider.Thumb className="block w-4 h-4 bg-accent border-2 border-white rounded-full shadow-lg hover:bg-primary-700 focus:outline-none " />
              </Slider.Root>
              <span className="ml-4 w-12 text-right font-medium text-gray-800">
                {interestRate}%
              </span>
            </div>
          </div>
          
          {/* Assistance Section */}
          <div className="mt-8">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Do you need any assistance? Would you like to know more about the loan?
            </p>
            <LoanRequestDialog>
              <button className="bg-accent text-white font-semibold py-2 px-4 rounded-md text-sm hover:bg-primary-700 transition-colors">
                Get Assistance
              </button>
            </LoanRequestDialog>
          </div>
        </div>

        {/* Right Section: Results */}
        <motion.div
          className="flex flex-col items-center justify-center text-center p-0"
          variants={resultsVariants}
          initial="hidden"
          animate="visible"
        >
          <h3 className="text-lg font-bold text-gray-800">Monthly Home Loan EMI</h3>
          <p className="text-5xl font-extrabold text-accent mt-2">
            {formatCurrency(monthlyEMI)}
          </p>
          <div className="w-full h-36 my-6">
            <svg viewBox="0 0 36 36" className="w-full h-full">
              <path
                className="text-gray-200 stroke-current"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                strokeWidth="2"
                strokeDasharray="100"
              />
              <path
                className="text-accent/60 stroke-current"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                strokeWidth="2"
                strokeDasharray={`${(loanAmount / totalPayment) * 100}, 100`}
              />
              <path
                className="text-accent stroke-current"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                strokeWidth="2"
                strokeDasharray={`${(totalInterest / totalPayment) * 100}, 100`}
                strokeDashoffset={`${(loanAmount / totalPayment) * 100}`}
              />
            </svg>
          </div>
          <div className="w-full text-left space-y-2 mb-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Principal Amount</span>
              <span className="font-semibold text-gray-900">{formatCurrency(loanAmount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Interest Amount</span>
              <span className="font-semibold text-gray-900">{formatCurrency(totalInterest)}</span>
            </div>
            <div className="flex justify-between items-center border-t pt-2 mt-2">
              <span className="text-sm font-bold">Total Amount Payable</span>
              <span className="text-lg font-extrabold text-gray-900">{formatCurrency(totalPayment)}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}